import { PAGINATION } from '@/constants/pagination.constant';
import { MessageType } from '@/types/common';
import { createAppSlice } from '../createAppSlice';
import { transformerArray, transformerObject } from '../transformer';
import { Message } from '@/enums/message.enum';
import { MESSAGE } from '@/constants/message.constant';
import {
  convertBatchFromBackendUnits,
  convertBatchToBackendUnits
} from '@/constants/food-nutrients.constant';

type FoodSliceState = {
  loading: boolean;
  loadingQuantitationFood: boolean;
  listFood: Food[];
  listGroupGood: GroupFood[];
  listGroupMaterialFood: GroupMaterialFood[];
  listFoodById?: Food;
  listFoodClassification: FoodClassification[];
  listFoodQuantitative: FoodQuantitative[];
  listHistoryFood: HistoryFood[];
  listHistoryGroupFood: HistoryFood[];
  message?: MessageType;
  pagination: Pagination;
  paginationHistory: Pagination;
  paginationHistoryGroupFood: Pagination;
  paginationGroupFood: Pagination;
  paginationFoodQuantitative: Pagination;
  fileUrl: string;
  image: string;
  downloadTemplateFood?: TemplateFood;
  downloadTemplateQuantitative?: TemplateFood;
};

const initialState: FoodSliceState = {
  loading: false,
  loadingQuantitationFood: false,
  listFood: [],
  listGroupGood: [],
  listGroupMaterialFood: [],
  listFoodClassification: [],
  listHistoryFood: [],
  listHistoryGroupFood: [],
  listFoodQuantitative: [],
  pagination: PAGINATION,
  paginationHistory: PAGINATION,
  paginationHistoryGroupFood: PAGINATION,
  paginationGroupFood: PAGINATION,
  paginationFoodQuantitative: PAGINATION,
  fileUrl: '',
  image: '',
  downloadTemplateFood: undefined,
  downloadTemplateQuantitative: undefined
};

export const foodSlice = createAppSlice({
  name: 'food',
  initialState,
  reducers: (create) => ({
    createFood: create.asyncThunk(
      async (body: any, { rejectWithValue }) => {
        try {
          const response = await apiFetcher.post('foods', body);
          // Convert backend unit to display unit when response returns
          const convertedData = convertBatchFromBackendUnits(
            response.data
          ) as Food;
          return convertedData;
        } catch (err: any) {
          const message = err?.response?.data?.message;
          return rejectWithValue(message);
        }
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.CREATE_FOOD_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          let errorMsg = MESSAGE.CREATE_FOOD_FAIL;
          if (typeof action.payload === 'string') {
            errorMsg = action.payload;
          }
          state.message = {
            type: Message.ERROR,
            message: errorMsg
          };
        }
      }
    ),
    updateFood: create.asyncThunk(
      async (body: any) => {
        console.log(body);
        // Convert display unit to backend storage unit before sending
        const convertedBody = {
          ...body,
          foodInfo: body.foodInfo
            ? convertBatchToBackendUnits(body.foodInfo)
            : body.foodInfo
        };
        console.log(convertedBody);
        const response = await apiFetcher.post('foods/update', convertedBody);
        // Convert backend unit to display unit when response returns
        const convertedData = convertBatchFromBackendUnits(
          response.data
        ) as Food;
        return convertedData;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.UPDATE_FOOD_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.UPDATE_FOOD_FAIL
          };
        }
      }
    ),
    getListFood: create.asyncThunk(
      async (payload?: {
        query?: FilterFood;
        offset?: number;
        limit?: number;
      }) => {
        const { query, offset = 1, limit = 10 } = payload || {};
        const params = new URLSearchParams();
        params.append('offset', offset.toString());
        params.append('limit', limit.toString());

        const body = {
          ...(query || {}),
          latestChangeOnTop: true
        };

        const url = `foods/filter?${params.toString()}`;

        const response = await apiFetcher.post(url, body || {});
        // Convert backend units to display units for each food in the list
        const convertedListData = (response.data.listData || []).map(
          (food: any) => convertBatchFromBackendUnits(food) as Food
        );
        return { ...response.data, listData: convertedListData };
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listFood = action.payload.listData;
          state.pagination = action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.listFood = [];
          state.pagination = PAGINATION;
        }
      }
    ),
    getDetailFood: create.asyncThunk(
      async (id: string) => {
        const response = await apiFetcher.get(`foods/${id}`);
        // Convert backend unit to display unit when response returns
        const convertedData = convertBatchFromBackendUnits(
          response.data
        ) as Food;
        return convertedData;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listFoodById = action.payload;
        },
        rejected: (state) => {
          state.loading = false;
          state.listFoodById = undefined;
        }
      }
    ),
    getListFoodQuantitative: create.asyncThunk(
      async (payload?: {
        offset?: number;
        limit?: number;
        foodId?: number;
      }) => {
        const { offset = 1, limit = 10, foodId } = payload || {};
        const params = new URLSearchParams();

        params.append('offset', offset.toString());
        params.append('limit', limit.toString());

        const url = `quantitative/list?${params.toString()}`;
        const body = foodId ? { foodId } : {};

        const response = await apiFetcher.post(url, body);
        // Convert backend units to display units for each quantitative food
        const convertedListData = (response.data.listData || []).map(
          (item: any) => convertBatchFromBackendUnits(item) as FoodQuantitative
        );
        return { ...response.data, listData: convertedListData };
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listFoodQuantitative = transformerObject(
            action.payload.listData
          );
          state.paginationFoodQuantitative = action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.listFoodQuantitative = [];
          state.paginationFoodQuantitative = PAGINATION;
        }
      }
    ),
    deleteFood: create.asyncThunk(
      async (id: string) => {
        try {
          const response = await apiFetcher.delete(`foods/${id}`);
          return response.data;
        } catch (error: any) {
          throw new Error(error?.data?.message ?? MESSAGE.DELETE_FOOD_FAIL);
        }
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listFood = state.listFood.filter(
            (food) => food.ID !== String(action.payload)
          );
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.DELETE_FOOD_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: action?.error?.message ?? MESSAGE.DELETE_FOOD_FAIL
          };
        }
      }
    ),
    createFoodQuantitative: create.asyncThunk(
      async (query?: Record<string, any>) => {
        const response = await apiFetcher.post('quantitative', query);
        return response.data;
      },
      {
        pending: (state) => {
          state.loadingQuantitationFood = true;
        },
        fulfilled: (state) => {
          state.loadingQuantitationFood = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.CREATE_QUANTITATION_FOOD_SUCCESS
          };
        },
        rejected: (state) => {
          state.loadingQuantitationFood = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.CREATE_QUANTITATION_FOOD_FAIL
          };
        }
      }
    ),
    deleteFoodQuantitative: create.asyncThunk(
      async (id: string | number) => {
        await apiFetcher.delete(`quantitative/${encodeURIComponent(String(id))}`);
        return id;
      },
      {
        pending: (state) => {
          state.loadingQuantitationFood = true;
        },
        fulfilled: (state, action) => {
          state.loadingQuantitationFood = false;
          state.listFoodQuantitative = state.listFoodQuantitative.filter(
            (food) => food.id !== action.payload
          );
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.DELETE_QUANTITATION_FOOD_SUCCESS
          };
        },
        rejected: (state) => {
          state.loadingQuantitationFood = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.DELETE_QUANTITATION_FOOD_FAIL
          };
        }
      }
    ),
    importFile: create.asyncThunk(
      async (formData: FormData) => {
        try {
          const response = await apiFetcher.post('foods/import', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          return response.data;
        } catch (error: any) {
          throw new Error(
            error?.response?.data?.message || error?.data?.message
          );
        }
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.IMPORT_FOOD_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          const errorMsg = action.error?.message?.toLowerCase() || '';
          if (
            errorMsg.includes('excel') ||
            errorMsg.includes('images') ||
            errorMsg.includes('template')
          ) {
            state.message = {
              type: Message.ERROR,
              message: MESSAGE.IMPORT_NUTRITION_TEMPLATE_ERROR
            };
          } else {
            state.message = {
              type: Message.ERROR,
              message: errorMsg
            };
          }
        }
      }
    ),
    getHistoryFood: create.asyncThunk(
      async ({ offset, limit }: { offset?: number; limit?: number }) => {
        const params = new URLSearchParams();
        if (offset !== undefined) params.append('page', offset.toString());
        if (limit !== undefined) params.append('limit', limit.toString());

        const url = `history-import/filter${params.toString() ? `?${params.toString()}` : ''}`;
        const query = {
          type: 'FOOD'
        };
        const response = await apiFetcher.post(url, query || {});
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listHistoryFood = transformerArray(action.payload.listData);
          state.paginationHistory = action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.listHistoryFood = [];
          state.paginationHistory = PAGINATION;
        }
      }
    ),
    getListGroupFood: create.asyncThunk(
      async (payload?: { offset?: number; limit?: number }) => {
        const { offset = 1, limit = 10 } = payload || {};

        const params = new URLSearchParams();
        params.append('page', offset.toString());
        params.append('limit', limit.toString());

        const url = `food-group/list?${params.toString()}`;
        const response = await apiFetcher.post(url);
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listGroupGood = transformerArray(action.payload.listData);
          state.paginationGroupFood = action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.listGroupGood = [];
          state.paginationGroupFood = PAGINATION;
        }
      }
    ),
    getListMaterialFood: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get(`food-material`);
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listGroupMaterialFood = transformerArray(
            action.payload.listData
          );
        },
        rejected: (state) => {
          state.loading = false;
          state.listGroupMaterialFood = [];
        }
      }
    ),
    getFoodClassification: create.asyncThunk(
      async (type?: string) => {
        const response = await apiFetcher.get(
          `nutrition-classification?type=${type}`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listFoodClassification = transformerArray(
            action.payload.listData
          );
        },
        rejected: (state) => {
          state.loading = false;
          state.listFoodClassification = [];
        }
      }
    ),
    updateGroupFood: create.asyncThunk(
      async (payload: DetailGroupFood) => {
        const response = await apiFetcher.post('food-group/update', payload);
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.UPDATE_GROUP_FOOD_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.UPDATE_GROUP_FOOD_FAIL
          };
        }
      }
    ),
    importFileQuantityFood: create.asyncThunk(
      async (formData: FormData) => {
        try {
          const response = await apiFetcher.post(
            'quantitative/import',
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          return response.data;
        } catch (error: any) {
          throw new Error(
            error?.response?.data?.message || error?.data?.message
          );
        }
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.IMPORT_FOOD_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          const errorMsg = action.error?.message?.toLowerCase() || '';
          if (
            errorMsg.includes('images cooked') ||
            errorMsg.includes('images raw') ||
            errorMsg.includes('excel')
          ) {
            state.message = {
              type: Message.ERROR,
              message: MESSAGE.IMPORT_NUTRITION_TEMPLATE_ERROR
            };
          } else {
            state.message = {
              type: Message.ERROR,
              message: action.error?.message || MESSAGE.IMPORT_FOOD_FAIL
            };
          }
        }
      }
    ),
    getHistoryQuantityFood: create.asyncThunk(
      async ({ offset, limit }: { offset?: number; limit?: number }) => {
        const params = new URLSearchParams();
        if (offset !== undefined) params.append('page', offset.toString());
        if (limit !== undefined) params.append('limit', limit.toString());

        const url = `history-import/filter${params.toString() ? `?${params.toString()}` : ''}`;
        const query = {
          type: 'QUANTITATIVE'
        };
        const response = await apiFetcher.post(url, query || {});
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listHistoryGroupFood = transformerArray(
            action.payload.listData
          );
          state.paginationHistoryGroupFood = action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.listHistoryGroupFood = [];
          state.paginationHistoryGroupFood = PAGINATION;
        }
      }
    ),
    uploadImage: create.asyncThunk(
      async (payload?: any) => {
        const response = await apiFetcher.post('upload-image', payload);
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.image = action.payload;
        },
        rejected: (state) => {
          state.loading = false;
          state.image = '';
        }
      }
    ),
    downloadTemplateFood: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('template/info?type=FOOD');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.downloadTemplateFood = action.payload;
        },
        rejected: (state) => {
          state.loading = false;
          state.downloadTemplateFood = undefined;
        }
      }
    ),
    downloadTemplateQuantitative: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get(
          'template/info?type=QUANTITATIVE'
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.downloadTemplateQuantitative = action.payload;
        },
        rejected: (state) => {
          state.loading = false;
          state.downloadTemplateQuantitative = undefined;
        }
      }
    ),
    resetMessages: create.reducer((state) => {
      state.message = undefined;
    })
  }),
  selectors: {
    watchListFood: (food) => food.listFood,
    watchListGroupFood: (food) => food.listGroupGood,
    watchListGroupMaterialFood: (food) => food.listGroupMaterialFood,
    watchListHistoryFood: (food) => food.listHistoryFood,
    watchListHistoryGroupFood: (food) => food.listHistoryGroupFood,
    watchListFoodQuantitative: (food) => food.listFoodQuantitative,
    watchListFoodClassification: (food) => food.listFoodClassification,
    watchDetailFoodById: (food) => food.listFoodById,
    watchFoodPagination: (food) => food.pagination,
    watchGroupFoodPagination: (food) => food.paginationGroupFood,
    watchFoodPaginationHistory: (food) => food.paginationHistory,
    watchFoodPaginationHistoryGroupFood: (food) =>
      food.paginationHistoryGroupFood,
    watchFoodQuantitativePagination: (food) => food.paginationFoodQuantitative,
    watchFoodLoading: (food) => food.loading,
    watchQuantitationFoodLoading: (food) => food.loadingQuantitationFood,
    watchImage: (food) => food.image,
    watchFoodMessage: (food) => food.message,
    watchDownloadTemplateFood: (food) => food.downloadTemplateFood,
    watchDownloadTemplateQuantitative: (food) =>
      food.downloadTemplateQuantitative
  }
});

export const {
  watchListFood,
  watchListGroupFood,
  watchListHistoryFood,
  watchListHistoryGroupFood,
  watchListFoodQuantitative,
  watchListGroupMaterialFood,
  watchDetailFoodById,
  watchFoodPagination,
  watchGroupFoodPagination,
  watchFoodPaginationHistory,
  watchFoodPaginationHistoryGroupFood,
  watchListFoodClassification,
  watchFoodQuantitativePagination,
  watchFoodLoading,
  watchQuantitationFoodLoading,
  watchImage,
  watchFoodMessage,
  watchDownloadTemplateFood,
  watchDownloadTemplateQuantitative
} = foodSlice.selectors;

export const {
  getListFood,
  getListGroupFood,
  importFile,
  importFileQuantityFood,
  createFood,
  getDetailFood,
  deleteFood,
  getListFoodQuantitative,
  getListMaterialFood,
  getHistoryFood,
  getHistoryQuantityFood,
  getFoodClassification,
  createFoodQuantitative,
  deleteFoodQuantitative,
  updateGroupFood,
  uploadImage,
  resetMessages,
  updateFood,
  downloadTemplateFood,
  downloadTemplateQuantitative
} = foodSlice.actions;
